import kaplay from "kaplay";
import "kaplay/global"; // expose KAPLAY's API as globals (scene, go, add, ...)
import { initAudio, toggleMute } from "./audio";

// Right-click is a game control (reload); stop the browser context menu.
window.addEventListener("contextmenu", (e) => e.preventDefault());

// M toggles mute globally (also counts as a gesture to start audio), unless a
// text field is focused (so typing a high-score name doesn't toggle mute).
window.addEventListener("keydown", (e) => {
  if ((e.key === "m" || e.key === "M") && document.activeElement?.tagName !== "INPUT") {
    initAudio();
    toggleMute();
  }
});

import { loadConfig } from "./config";
import { THEMES, NIGHT_THEME, paintBackgroundDataURL, setExteriorArt, deriveExteriorSlots, setHallArt } from "./render/background";
import { processHouseArt } from "./render/houseArt";
import { processHallArt } from "./render/hallArt";
import { processGunArt } from "./render/gunArt";
import { paintGunDataURL } from "./render/gunSprite";
import { createMenuScene } from "./scenes/menu";
import { createGameScene } from "./scenes/game";
import { computeView } from "./viewport";

// Responsive "cover" mount: KAPLAY renders the fixed 640x448 buffer into a stage
// div sized so the scene fills the screen height; on tall devices the stage is
// wider than the viewport and its sides are clipped (body overflow:hidden),
// cropping the side decoration while keeping all spawn points visible. The
// scale never crops past the safe band (see viewport.ts). On wide screens this
// behaves like the old letterbox (fills height, side bars).
document.documentElement.style.overflow = "hidden";
document.body.style.overflow = "hidden";
document.body.style.background = "#000"; // fills any clipped/bar area
const stage = document.createElement("div");
stage.style.cssText = "position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);overflow:hidden";
document.body.appendChild(stage);
function layoutStage() {
  const s = computeView(window.innerWidth, window.innerHeight);
  stage.style.width = 640 * s + "px";
  stage.style.height = 448 * s + "px";
}
layoutStage();
window.addEventListener("resize", layoutStage);

// Fixed low-res buffer, sharp pixels. Author all art/coords/hitboxes in 640x448
// space; KAPLAY scales it into the stage. (640x448 = 2x the brief's 320x224.)
const k = kaplay({
  width: 640,
  height: 448,
  letterbox: true, // exact-fit into the (game-aspect) stage; no internal bars
  root: stage,
  crisp: true, // sharp pixel display (no antialias)
  texFilter: "nearest",
  font: "press2p", // crisp pixel font for all text (loaded below)
  background: [22, 10, 46], // deep night purple
  touchToMouse: false, // keep touch + mouse independent (mobile uses touch handlers)
});

// Crisp retro pixel font (OFL). KAPLAY shows a loading screen until it's ready,
// so scenes can use it immediately.
k.loadFont("press2p", "fonts/PressStart2P.ttf");

// Process the uploaded house.png into exterior cutouts (house/moon/tree + auto-
// detected window slots). On any failure this is null and the exterior falls
// back to the procedural house. Must run before baking the level sprites.
const houseArt = await processHouseArt("house.png");
if (houseArt) {
  setExteriorArt(houseArt);
  NIGHT_THEME.slots = deriveExteriorSlots(houseArt); // align spawn slots to the painted windows
}

// Level 2: composite the gothic hall + its foreground floor (null -> procedural).
setHallArt(await processHallArt("2ndLEvel-update.png", "2ndLEvel-ground.png"));

// Bake each level's static scene art into its own sprite ("bg0", "bg1", ...).
THEMES.forEach((t, i) => k.loadSprite("bg" + i, paintBackgroundDataURL(t)));
// First-person shotgun frames from the uploaded art (pump forward + racked back
// for the reload animation); fall back to the procedural sprite on failure.
const gun0 = await processGunArt("gun0.png", false); // shooting (pump forward), already transparent
const gun1 = await processGunArt("gun1.png", false); // mid reload frame, already transparent
const gunReload = await processGunArt("Gunreload.png", false); // racked back, already transparent
k.loadSprite("gun", gun0 ?? paintGunDataURL());
k.loadSprite("gun1", gun1 ?? gun0 ?? paintGunDataURL());
k.loadSprite("gun_reload", gunReload ?? gun0 ?? paintGunDataURL());

// Load runtime tuning before building scenes (defaults on failure, never throws).
const config = await loadConfig();
console.log("[config] loaded:", config);

// Register scenes, then enter the title.
createMenuScene(config);
createGameScene(config);

// Dev affordance: open with #game (optionally "#game?aim=x,y") to land directly
// in the play scene.
go(location.hash.startsWith("#game") ? "game" : "menu");
